import React, { useEffect, useState } from "react";
import { whatsappUrl } from "../../config/site";

/** Official WhatsApp glyph. */
export const WhatsAppIcon = ({ className = "h-6 w-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.548 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

/**
 * Floating WhatsApp entry point.
 *
 * - Appears only after the visitor has scrolled past the hero, so it
 *   never sits on top of the opening statement.
 * - Sits bottom-right where people expect it, respecting safe-area insets
 *   so it never collides with iOS home-indicator UI.
 * - Expands to show a label on hover/focus on pointer devices; stays a
 *   compact circle on touch so it covers as little as possible.
 * - Uses the documented wa.me click-to-chat format, which opens WhatsApp
 *   Web / the desktop app on desktop and the native app on mobile.
 */
export default function WhatsAppButton({
  message,
  className = "",
}) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Reveal after roughly one viewport of scrolling.
      setShown(window.scrollY > Math.min(560, window.innerHeight * 0.7));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <a
      href={whatsappUrl(message)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with DepthLogix on WhatsApp"
      data-shown={shown ? "true" : "false"}
      className={`group fixed z-40 flex items-center gap-0 overflow-hidden rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_-8px_rgba(37,211,102,0.6)]
        bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-[max(1.25rem,env(safe-area-inset-right))]
        h-14 w-14 justify-center
        transition-[opacity,transform,width,box-shadow] duration-300 ease-out-expo
        hover:shadow-[0_14px_38px_-8px_rgba(37,211,102,0.75)]
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#25D366]
        motion-safe:hover:-translate-y-0.5
        md:hover:w-[15.5rem] md:hover:justify-start md:hover:gap-3 md:hover:pl-4 md:hover:pr-5
        md:focus-visible:w-[13.5rem] md:focus-visible:justify-start md:focus-visible:gap-3 md:focus-visible:pl-4 md:focus-visible:pr-5
        ${shown ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0"}
        ${className}`}
    >
      <span className="relative grid h-8 w-8 flex-none place-items-center">
        {/* Soft pulse, motion-safe only */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-white/30 opacity-0 motion-safe:group-hover:animate-ping"
        />
        <WhatsAppIcon className="relative h-7 w-7" />
      </span>
      <span className="hidden whitespace-nowrap text-sm font-semibold md:group-hover:inline md:group-focus-visible:inline">
        Message us on WhatsApp
      </span>
    </a>
  );
}

/**
 * Inline WhatsApp button for use inside page content (contact page,
 * CTA blocks). Styled to sit in the DepthLogix system rather than
 * shouting in WhatsApp green.
 */
export function WhatsAppLink({
  message,
  children = "Chat on WhatsApp",
  variant = "solid",
  className = "",
}) {
  const styles =
    variant === "solid"
      ? "bg-[#25D366] text-white hover:bg-[#1fb855] shadow-card hover:shadow-card-hover"
      : "border border-ink-200 bg-white text-ink-900 hover:border-[#25D366] hover:text-[#128C4A] shadow-card hover:shadow-card-hover";

  return (
    <a
      href={whatsappUrl(message)}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center justify-center gap-2.5 rounded-xl px-6 py-3.5 font-semibold transition-[background-color,border-color,box-shadow,transform] duration-200 motion-safe:hover:-translate-y-0.5 ${styles} ${className}`}
    >
      <WhatsAppIcon className="h-5 w-5" />
      {children}
    </a>
  );
}
